import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { cssVariables, numberOrZero } from "@/utils/utils";
import { Card, Col, Descriptions, Divider, Popover, Row, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDownloadSpecialCommissionsExport, useGetSpecialCommissionsReport } from "../../../../../common/api/queries";
import { TableSizes } from "../../../../../common/constants";
import { ExportFileType, TopAgentType } from "../../../../../common/enums";
import { Permission } from "../../../../../common/security/enums";
import { SuperiorAgentsTable } from "../../../../agent/components/tables/SuperiorAgentsTable";
import { AgentTypeTagWithName } from "../../../../agent/components/tags/AgentTypeTagWithName";
import { AgentList } from "../../../../agent/types";
import { formatAgentIdNumber } from "../../../../agent/utils";
import { useHasPermissions } from "../../../../auth/hooks";
import { CommissionBatchName } from "../../../components/CommissionBatchName";
import { formatCommissionsLevelName } from "../../../level/utils";
import { SpecialCommissionList, SpecialCommissionsReportFilterPageRequest } from "../../types";

type ExpandedRowContentProps = {
  payer: AgentList;
  payee: AgentList;
};

const ExpandedRowContent = ({ payer, payee }: ExpandedRowContentProps) => {
  const { t } = useTranslation();

  const isPayerTopAgentPlus = payer.topAgentType === TopAgentType.PLUS;
  const isPayeeTopAgentPlus = payee.topAgentType === TopAgentType.PLUS;

  return (
    <Row gutter={24} style={{ padding: "8px 40px" }}>
      <Col span={12}>
        <Descriptions
          styles={{ title: { fontSize: 14 }, header: { marginBottom: 8 } }}
          bordered
          size="small"
          title={t("commissions.special.attrs.payer")}
          column={1}
          items={[
            {
              key: "status",
              label: isPayerTopAgentPlus
                ? t("agent.helpers.agentStatusSFAPlusLabel")
                : t("agent.enums.agentStatus._label"),
              children: payer.status ? t(`agent.enums.agentStatus.${payer.status}`) : "",
            },
            isPayerTopAgentPlus
              ? {
                  key: "secondaryStatus",
                  label: t("agent.helpers.agentSecondaryStatusSFAPlusLabel"),
                  children: payer.secondaryStatus
                    ? t(`agent.enums.agentSecondaryStatus.${payer.secondaryStatus}`)
                    : undefined,
                }
              : undefined,
            {
              key: "level",
              label: t("commissions.settings.attrs.level"),
              children: payer.commissionsSettings.level ? (
                formatCommissionsLevelName(payer.commissionsSettings.level)
              ) : (
                <span className="sub-header-info dashed">{t("common.none")}</span>
              ),
            },
            {
              key: "rank",
              label: (
                <>
                  {t("commissions.settings.attrs.rank")}

                  <span className="margin-left-tiny">
                    <Popover
                      title={t("agent.titles.superiorAgents")}
                      styles={{ root: { maxWidth: 580 } }}
                      content={<SuperiorAgentsTable agent={payer} />}
                    >
                      <span>
                        <AntIcon color="blue" type="cluster" />
                      </span>
                    </Popover>
                  </span>
                </>
              ),
              children: payer.commissionsSettings.rank?.name ?? (
                <span className="sub-header-info dashed">{t("common.none")}</span>
              ),
            },
          ].filter((item) => !!item)}
        />
      </Col>
      <Col span={12}>
        <Descriptions
          styles={{ title: { fontSize: 14 }, header: { marginBottom: 8 } }}
          bordered
          size="small"
          title={t("commissions.special.attrs.payee")}
          column={1}
          items={[
            {
              key: "status",
              label: isPayeeTopAgentPlus
                ? t("agent.helpers.agentStatusSFAPlusLabel")
                : t("agent.enums.agentStatus._label"),
              children: payee.status ? t(`agent.enums.agentStatus.${payee.status}`) : "",
            },
            isPayeeTopAgentPlus
              ? {
                  key: "secondaryStatus",
                  label: t("agent.helpers.agentSecondaryStatusSFAPlusLabel"),
                  children: payee.secondaryStatus
                    ? t(`agent.enums.agentSecondaryStatus.${payee.secondaryStatus}`)
                    : undefined,
                }
              : undefined,
            {
              key: "level",
              label: t("commissions.settings.attrs.level"),
              children: payee.commissionsSettings.level ? (
                formatCommissionsLevelName(payee.commissionsSettings.level)
              ) : (
                <span className="sub-header-info dashed">{t("common.none")}</span>
              ),
            },
            {
              key: "rank",
              label: (
                <>
                  {t("commissions.settings.attrs.rank")}

                  <span className="margin-left-tiny">
                    <Popover
                      title={t("agent.titles.superiorAgents")}
                      styles={{ root: { maxWidth: 580 } }}
                      content={<SuperiorAgentsTable agent={payee} />}
                    >
                      <span>
                        <AntIcon color="blue" type="cluster" />
                      </span>
                    </Popover>
                  </span>
                </>
              ),
              children: payee.commissionsSettings.rank?.name ?? (
                <span className="sub-header-info dashed">{t("common.none")}</span>
              ),
            },
          ].filter((item) => !!item)}
        />
      </Col>
    </Row>
  );
};

const getColumns = (showBatchLink?: boolean): ColumnsType<SpecialCommissionList> => {
  return [
    {
      key: "commissionsBatch",
      title: t("commissions.bailAccount.attrs.movements.commissionsBatch"),
      width: 170,
      ellipsis: { showTitle: true },
      render: (_, record) => (
        <CommissionBatchName batchId={record.batch.id} showLink={showBatchLink} batchName={record.batch.name} />
      ),
    },
    {
      key: "payer",
      title: t("commissions.special.attrs.payer"),
      children: [
        {
          key: "payerName",
          title: t("common.name"),
          ellipsis: { showTitle: false },
          width: 185,
          render: (_, record) => (
            <AgentTypeTagWithName
              textStyle={record.payer.deactivated ? { color: cssVariables.colorRed } : undefined}
              agent={record.payer}
              hideIdNumber
              ellipsis
              showLink
              showRepresentatives
            />
          ),
        },
        {
          key: "payerIdNumber",
          title: t("agent.attrs.idNumber"),
          ellipsis: { showTitle: false },
          width: 90,
          render: (_, record) => <Ellipsis>{formatAgentIdNumber(record.payer)}</Ellipsis>,
        },
      ],
    },
    {
      key: "payee",
      title: t("commissions.special.attrs.payee"),
      children: [
        {
          key: "payeeName",
          title: t("common.name"),
          ellipsis: { showTitle: false },
          width: 185,
          render: (_, record) => (
            <AgentTypeTagWithName
              textStyle={record.payee.deactivated ? { color: cssVariables.colorRed } : undefined}
              agent={record.payee}
              hideIdNumber
              ellipsis
              showLink
              showRepresentatives
            />
          ),
        },
        {
          key: "payeeIdNumber",
          title: t("agent.attrs.idNumber"),
          ellipsis: { showTitle: false },
          width: 90,
          render: (_, record) => <Ellipsis>{formatAgentIdNumber(record.payee)}</Ellipsis>,
        },
      ],
    },
    {
      key: "code",
      title: t("commissions.special.attrs.code"),
      width: 100,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.code}</Ellipsis>,
    },
    {
      key: "name",
      title: t("commissions.special.attrs.name"),
      width: 185,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.name}</Ellipsis>,
    },
    {
      key: "commissionAmount",
      title: t("commissions.special.attrs.commissionAmount"),
      width: 110,
      align: "right",
      render: (_, record) => formatLocaleCurrency(record.commissionAmount),
    },
  ];
};

type Props = {
  searchParams: SpecialCommissionsReportFilterPageRequest;
};

export const SpecialCommissionsReportTable = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const hasCommissionsPermission = useHasPermissions(Permission.COMMISSIONS);

  const { data } = useGetSpecialCommissionsReport(searchParams);
  const downloadSpecialCommissionsExport = useDownloadSpecialCommissionsExport();

  return (
    <Card className="card-box">
      <div className={data?.totalElementsCount ? "table-header-export-actions" : "margin-bottom-small"}>
        <ActionTextIcon
          icon="download"
          color="blue"
          text={t("common.exportXlsx")}
          onClick={() =>
            downloadSpecialCommissionsExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })
          }
        />

        <Divider orientation="vertical" />

        <ActionTextIcon
          icon="download"
          color="green"
          text={t("common.exportCsv")}
          onClick={() =>
            downloadSpecialCommissionsExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })
          }
        />
      </div>

      <Table<SpecialCommissionList>
        {...tableStandardProps()}
        columns={getColumns(hasCommissionsPermission)}
        scroll={{ x: TableSizes.LARGE }}
        dataSource={data?.pageData}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
        expandable={{
          expandedRowRender: (record) => <ExpandedRowContent payee={record.payee} payer={record.payer} />,
          columnWidth: 35,
        }}
      />
    </Card>
  );
};
